import { faCheckCircle, faReply } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useContext, useMemo, useState } from 'react';

import ToggleText from '~/components/ui/ToggleText';
import { useUserStatus } from '~/hooks/userStatus';
import { MAX_LEVEL_REPLY_ALLOWED } from '~/lib/discussions/constants';
import {
  DISCUSSION_STATUS_APPROVED,
  DiscussionStatus,
  IQuestionCommentProps,
} from '~/lib/discussions/types';
import { useEventDispatch } from '~/lib/events';
import { PDPContext } from '~/lib/product/context';
import useUser from '~/lib/user';
import { Deal } from '~/types/deal';
import DiscussionTag from './DiscussionTag';
import DiscussionUserInfo from './DiscussionUserInfo';
import QuestionCommentInput from './QuestionCommentInput';

function QuestionComment({
  id,
  dealId,
  text,
  level = 0,
  status,
  comments = [],
  userInfo,
}: Readonly<IQuestionCommentProps>) {
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [hasReply, setHasReply] = useState(false);

  const { user } = useUser();
  const { userStatus } = useUserStatus(dealId);
  const { deal }: { deal: Deal } = useContext(PDPContext);

  const dispatchEvent = useEventDispatch();

  const isPartner = useMemo(() => {
    return deal?.partner_user_ids?.includes(user?.id ?? 0);
  }, [deal?.partner_user_ids, user?.id]);

  const showDiscussionTag = useMemo(() => {
    // owner, staff or partner and not approved
    return (
      (user?.id === userInfo.id || userStatus?.staff_moderator || isPartner) &&
      status?.toLowerCase() !== DISCUSSION_STATUS_APPROVED
    );
  }, [isPartner, status, user?.id, userInfo.id, userStatus?.staff_moderator]);

  const onSuccess = () => {
    // display message only for unmoderated comments
    setHasReply(true);
    dispatchEvent('comment:mutate');
  };

  return (
    <div
      id={id.toString()}
      data-testid="question-comment"
      className="space-y-4"
    >
      <div className="space-y-2 border-l-2 border-sundog pl-3">
        {showDiscussionTag && (
          <DiscussionTag
            isStaffOrPartner={userStatus?.user_moderator || isPartner}
            isOwner={user?.id === userInfo.id}
            status={status?.toLowerCase() as DiscussionStatus}
          />
        )}

        <DiscussionUserInfo {...userInfo} />

        <ToggleText text={text} />

        {user?.is_authenticated &&
          level < MAX_LEVEL_REPLY_ALLOWED &&
          !showReplyInput && (
            <button
              data-testid="reply-button"
              type="button"
              className="text-sm font-medium text-bolt hover:text-bolt-20"
              onClick={() => setShowReplyInput(true)}
            >
              <FontAwesomeIcon icon={faReply} className="mr-1" />
              Reply
            </button>
          )}

        {showReplyInput && !hasReply && (
          <QuestionCommentInput
            dealId={dealId}
            comment={{ id }}
            placeholder="Add a reply..."
            onCancel={() => setShowReplyInput(false)}
            onSuccess={onSuccess}
            enableAutoFocus
          />
        )}
      </div>

      {hasReply && (
        <div
          data-testid="thread-success-message"
          className="flex gap-2 rounded-sm bg-money-moss p-4 px-2 text-sm text-white"
        >
          <FontAwesomeIcon icon={faCheckCircle} className="pt-1 text-lg" />
          <p>
            <b>Thank you for submitting!</b> To keep our community as helpful as
            possible, we moderate all comments and reviews.
          </p>
        </div>
      )}

      {level < MAX_LEVEL_REPLY_ALLOWED && (comments?.length ?? 0) > 0 && (
        <div className="space-y-4 pl-4">
          {comments?.map((comment: IQuestionCommentProps) => (
            <QuestionComment key={comment.id} {...comment} />
          ))}
        </div>
      )}
    </div>
  );
}

export default QuestionComment;
